import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  TooltipProps,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { TotalSalesNewVsExistingCustomerChartDataItem } from "@/modules/stochastic/features/Archived/DashboardPage/api/fetchTotalSalesNewVsExistingCustomerChartData/types";
import { formatCurrency } from "@/modules/stochastic/features/SalesAndCustomerDashboard/utils/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface CustomTooltipProps extends TooltipProps<number, string> {
  theme: string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  theme,
}: CustomTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  const ncValue = parseFloat(
    payload.find((p) => p.dataKey === "newCustomer")?.value?.toString() || "0",
  );
  const hfValue = parseFloat(
    payload.find((p) => p.dataKey === "existingCustomer")?.value?.toString() ||
      "0",
  );
  const total = ncValue + hfValue;

  const ncPercentage = total > 0 ? ((ncValue / total) * 100).toFixed(1) : "0.0";
  const hfPercentage = total > 0 ? ((hfValue / total) * 100).toFixed(1) : "0.0";

  return (
    <div
      className="rounded-lg shadow-lg p-3 border"
      style={{
        backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
        borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
        color: theme === "dark" ? "#F3F4F6" : "#1F2937",
      }}
    >
      <p className="font-semibold mb-2">{`Year: ${label}`}</p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{
                backgroundColor: theme === "dark" ? "#F87171" : "#EF4444",
              }}
            />
            <span className="text-sm">New Customer:</span>
          </div>
          <div className="text-right">
            <div className="font-medium">{formatCurrency(ncValue)}</div>
            <div className="text-xs opacity-75">{ncPercentage}%</div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{
                backgroundColor: theme === "dark" ? "#FB923C" : "#F59E0B",
              }}
            />
            <span className="text-sm">Existing Customer:</span>
          </div>
          <div className="text-right">
            <div className="font-medium">{formatCurrency(hfValue)}</div>
            <div className="text-xs opacity-75">{hfPercentage}%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

type ChartProps = {
  chartId?: string;
  initialData: TotalSalesNewVsExistingCustomerChartDataItem[];
};

export default function TotalSalesNewVsExistingCustomerChart({
  initialData,
}: ChartProps) {
  const { theme } = useTheme();

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Sales New vs. Existing Customer
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Shows whether growth is being driven by new market capture or
                  repeat demand within your existing customer base.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sales split between new customer acquisition and existing customer
          revenue
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer height={500} width="100%">
          <BarChart
            data={initialData}
            margin={{
              bottom: 5,
              left: 45,
              right: 30,
              top: 20,
            }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="year"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
            />
            <YAxis
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={formatCurrency}
            />
            <ChartTooltip content={<CustomTooltip theme={theme} />} />
            <Legend />
            <Bar
              dataKey="newCustomer"
              fill={theme === "dark" ? "#F87171" : "#EF4444"}
              name="New Customer"
              stackId="a"
            />
            <Bar
              dataKey="existingCustomer"
              fill={theme === "dark" ? "#FB923C" : "#F59E0B"}
              name="Existing Customer"
              stackId="a"
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
